
import React, { useMemo, useState } from 'react';
import type { TimeOffRequest, TimeOffRequestStatus, AssignmentStatus, StaffMember, TimeOffRequestType } from '../../types';
import { Icons } from '../ui/Icons';
import Popover from '../ui/Popover';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';

interface TimeOffManagementViewProps {
    requests: TimeOffRequest[];
    staffList: StaffMember[];
    onUpdateRequestStatus: (requestId: string, status: 'APPROVED' | 'REJECTED', scheduleStatus?: AssignmentStatus) => void;
    onDeleteRequest: (requestId: string) => void;
    onCreateRequest: (request: Omit<TimeOffRequest, 'id' | 'userId' | 'status' | 'createdAt' | 'staffName'>) => void;
    onUpdateRequest: (requestId: string, data: Partial<TimeOffRequest>) => void;
    canAddDelete?: boolean;
    canEditApprove?: boolean;
}

const statusConfig: { [key in TimeOffRequestStatus]: { text: string; bg: string; icon: React.ReactNode; label: string; } } = {
    PENDING_REPLACER: { text: 'text-orange-800 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/25', icon: <Icons.Users className="w-4 h-4" />, label: 'Pending Cover' },
    PENDING_ADMIN: { text: 'text-yellow-800 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/25', icon: <Icons.Clock className="w-4 h-4" />, label: 'Pending Admin' },
    APPROVED: { text: 'text-green-800 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/25', icon: <Icons.CalendarCheck className="w-4 h-4" />, label: 'Approved' },
    REJECTED: { text: 'text-red-800 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-900/25', icon: <Icons.X className="w-4 h-4" />, label: 'Rejected' },
};

const TimeOffManagementView: React.FC<TimeOffManagementViewProps> = ({ 
    requests, 
    staffList, 
    onUpdateRequestStatus, 
    onDeleteRequest, 
    onCreateRequest, 
    onUpdateRequest,
    canAddDelete = true,
    canEditApprove = true
}) => {
    
    const staffMap = useMemo(() => new Map(staffList.map(s => [s.id, s.name])), [staffList]);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState<TimeOffRequest | null>(null);
    const [newRequest, setNewRequest] = useState<{
        employeeId: string;
        startDate: string;
        endDate: string;
        reason: string;
        type: TimeOffRequestType;
        replacementEmployeeId: string;
    }>({
        employeeId: '',
        startDate: '',
        endDate: '',
        reason: '',
        type: 'DIRECT',
        replacementEmployeeId: ''
    });

    const {
        itemToDelete,
        isDeleting,
        initiateDelete,
        confirmDelete,
        cancelDelete
    } = useDeleteHandler<TimeOffRequest>({
        onDeleteAsync: async (req) => onDeleteRequest(req.id),
        entityName: 'Time Off Request',
        getDisplayName: (req) => `for ${req.staffName}`,
        getItemId: (req) => req.id,
    });


    const sortedRequests = requests
        .filter(req => req.status !== 'deleted' as any)
        .sort((a, b) => {
            // Pending requests first, then by date
            const aIsPending = a.status?.startsWith('PENDING');
            const bIsPending = b.status?.startsWith('PENDING');
            if (aIsPending && !bIsPending) return -1;
            if (!aIsPending && bIsPending) return 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

    const approvalOptions: { label: string, status: AssignmentStatus, icon: React.ReactNode }[] = [
        { label: 'As Vacation', status: 'VACATION', icon: <Icons.Plane className="w-4 h-4 text-blue-500" /> },
        { label: 'As Sick Day', status: 'SICK', icon: <Icons.Thermometer className="w-4 h-4 text-yellow-500" /> },
        { label: 'As Off Day', status: 'OFF', icon: <Icons.Coffee className="w-4 h-4 text-gray-500" /> },
        { label: 'As Refused', status: 'REFUSED', icon: <Icons.XCircle className="w-4 h-4 text-purple-500" /> },
    ];
    
    const ApprovalPopoverContent = ({ close, requestId }: { close: () => void; requestId: string }) => (
        <div className="w-48">
            <div className="p-2 text-sm font-semibold text-gray-600 dark:text-gray-300 border-b dark:border-slate-700">Approve request as:</div>
             <div className="p-1">
                {approvalOptions.map(({ label, status, icon }) => (
                    <button
                        key={status}
                        onClick={() => {
                            onUpdateRequestStatus(requestId, 'APPROVED', status);
                            close();
                        }}
                        className="w-full flex items-center gap-3 text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
                    >
                        {icon}
                        <span>{label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onCreateRequest({
            employeeId: newRequest.employeeId,
            startDate: newRequest.startDate,
            endDate: newRequest.endDate,
            reason: newRequest.reason,
            type: newRequest.type,
            replacementEmployeeId: newRequest.type === 'REPLACED' ? newRequest.replacementEmployeeId : null
        });
        setIsCreateModalOpen(false);
        setNewRequest({
            employeeId: '',
            startDate: '',
            endDate: '',
            reason: '',
            type: 'DIRECT',
            replacementEmployeeId: ''
        });
    };

    const handleEditSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingRequest) return;
        
        onUpdateRequest(editingRequest.id, {
            employeeId: editingRequest.employeeId,
            startDate: editingRequest.startDate,
            endDate: editingRequest.endDate,
            reason: editingRequest.reason,
            type: editingRequest.type,
            replacementEmployeeId: editingRequest.type === 'REPLACED' ? editingRequest.replacementEmployeeId : null
        });
        setEditingRequest(null);
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Time Off Requests</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Review and approve or reject time off requests from staff.</p>
                </div>
                {canAddDelete && (
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium w-full sm:w-auto justify-center"
                    >
                        <Icons.Plus className="w-4 h-4" />
                        Create Request
                    </button>
                )}
            </div>
             <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead className="bg-gray-50 dark:bg-slate-900/50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff Member</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                                <th scope="col" className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                <th scope="col" className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Replacement</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                             {sortedRequests.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-10 text-gray-500 dark:text-gray-400">
                                        There are no time off requests.
                                    </td>
                                </tr>
                            )}
                            {sortedRequests.map((req) => (
                                <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 group">
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{req.staffName}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px] sm:max-w-xs">{req.reason || 'No reason provided'}</div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                        <div className="flex flex-col">
                                            <span>{req.startDate}</span>
                                            <span className="text-[10px] text-gray-400">to</span>
                                            <span>{req.endDate}</span>
                                        </div>
                                    </td>
                                    <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${req.type === 'DIRECT' ? 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/25 dark:text-blue-300'}`}>
                                            {req.type}
                                        </span>
                                    </td>
                                    <td className="hidden lg:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                        {req.type === 'REPLACED' && req.replacementEmployeeId ? staffMap.get(req.replacementEmployeeId) || 'Unknown Staff' : 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                                        {statusConfig[req.status] ? (
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[req.status].bg} ${statusConfig[req.status].text}`}>
                                                {statusConfig[req.status].icon}
                                                <span className="hidden sm:inline">{statusConfig[req.status].label}</span>
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {req.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <div className="flex justify-center items-center gap-2">
                                            {canEditApprove && (req.status === 'PENDING_ADMIN' || req.status === 'PENDING_REPLACER') && (
                                                <>
                                                    <Popover
                                                        content={<ApprovalPopoverContent requestId={req.id} close={() => {}} />}
                                                    >
                                                        <button className="px-2 sm:px-3 py-1 rounded-md text-[10px] sm:text-xs bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/25 dark:text-green-300 dark:hover:bg-green-900/40 flex items-center gap-1 shadow-sm">
                                                            <span className="hidden sm:inline">Approve</span>
                                                            <Icons.Check className="w-3 h-3 sm:hidden" />
                                                            <Icons.ChevronDown className="w-3 h-3" />
                                                        </button>
                                                    </Popover>
                                                    <button 
                                                        onClick={() => onUpdateRequestStatus(req.id, 'REJECTED')} 
                                                        className="px-2 sm:px-3 py-1 rounded-md text-[10px] sm:text-xs bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/25 dark:text-red-300 dark:hover:bg-red-900/40 shadow-sm"
                                                        title="Reject"
                                                    >
                                                        <span className="hidden sm:inline">Reject</span>
                                                        <Icons.X className="w-3 h-3 sm:hidden" />
                                                    </button>
                                                </>
                                            )}
                                            <div className="flex items-center gap-1 sm:ml-2 border-l border-gray-200 dark:border-slate-700 pl-1 sm:pl-2">
                                                {canEditApprove && (
                                                    <button 
                                                        onClick={() => setEditingRequest(req)}
                                                        className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400 transition-colors"
                                                        title="Edit Request"
                                                    >
                                                        <Icons.Edit className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {canAddDelete && (
                                                    <button 
                                                        onClick={() => initiateDelete(req)}
                                                        className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                                                        title="Delete Request"
                                                    >
                                                        <Icons.Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Time Off Request</h3>
                            <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit} className="p-6 space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Staff Member</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Icons.User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <select
                                        required
                                        value={newRequest.employeeId}
                                        onChange={(e) => setNewRequest({ ...newRequest, employeeId: e.target.value })}
                                        className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                    >
                                        <option value="">Select an employee</option>
                                        {staffList.filter(s => s.status !== 'deleted').map(staff => (
                                            <option key={staff.id} value={staff.id}>{staff.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Start Date</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={newRequest.startDate}
                                            onChange={(e) => setNewRequest({ ...newRequest, startDate: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">End Date</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={newRequest.endDate}
                                            onChange={(e) => setNewRequest({ ...newRequest, endDate: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Request Type</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => setNewRequest({ ...newRequest, type: 'DIRECT', replacementEmployeeId: '' })}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                                            newRequest.type === 'DIRECT' 
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                                            : 'border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 text-gray-500 dark:text-gray-400'
                                        }`}
                                    >
                                        <Icons.UserCheck className="w-4 h-4" />
                                        <span className="text-sm font-bold">Direct</span>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setNewRequest({ ...newRequest, type: 'REPLACED' })}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                                            newRequest.type === 'REPLACED' 
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                                            : 'border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 text-gray-500 dark:text-gray-400'
                                        }`}
                                    >
                                        <Icons.Users className="w-4 h-4" />
                                        <span className="text-sm font-bold">Replaced</span>
                                    </button>
                                </div>
                            </div>

                            {newRequest.type === 'REPLACED' && (
                                <div className="animate-in slide-in-from-top-2 duration-200">
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Replacement Staff</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.UserPlus className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <select
                                            required
                                            value={newRequest.replacementEmployeeId}
                                            onChange={(e) => setNewRequest({ ...newRequest, replacementEmployeeId: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        >
                                            <option value="">Select a replacement</option>
                                            {staffList
                                                .filter(s => s.status !== 'deleted' && s.id !== newRequest.employeeId)
                                                .map(staff => (
                                                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Reason (optional)</label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 text-gray-400">
                                        <Icons.FileText className="w-4 h-4" />
                                    </div>
                                    <textarea 
                                        value={newRequest.reason} 
                                        onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })} 
                                        rows={2} 
                                        placeholder="Admin notes or reason..."
                                        className="block w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="pt-2 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2"
                                >
                                    <Icons.Plus className="w-4 h-4" />
                                    Create Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {editingRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Time Off Request</h3>
                            <button onClick={() => setEditingRequest(null)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleEditSubmit} className="p-6 space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Staff Member</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Icons.User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <select
                                        required
                                        value={editingRequest.employeeId}
                                        onChange={(e) => setEditingRequest({ ...editingRequest, employeeId: e.target.value })}
                                        className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                    >
                                        <option value="">Select an employee</option>
                                        {staffList.filter(s => s.status !== 'deleted').map(staff => (
                                            <option key={staff.id} value={staff.id}>{staff.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Start Date</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={editingRequest.startDate}
                                            onChange={(e) => setEditingRequest({ ...editingRequest, startDate: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">End Date</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={editingRequest.endDate}
                                            onChange={(e) => setEditingRequest({ ...editingRequest, endDate: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Request Type</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => setEditingRequest({ ...editingRequest, type: 'DIRECT', replacementEmployeeId: null })}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                                            editingRequest.type === 'DIRECT' 
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                                            : 'border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 text-gray-500 dark:text-gray-400'
                                        }`}
                                    >
                                        <Icons.UserCheck className="w-4 h-4" />
                                        <span className="text-sm font-bold">Direct</span>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setEditingRequest({ ...editingRequest, type: 'REPLACED' })}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                                            editingRequest.type === 'REPLACED' 
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                                            : 'border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 text-gray-500 dark:text-gray-400'
                                        }`}
                                    >
                                        <Icons.Users className="w-4 h-4" />
                                        <span className="text-sm font-bold">Replaced</span>
                                    </button>
                                </div>
                            </div>

                            {editingRequest.type === 'REPLACED' && (
                                <div className="animate-in slide-in-from-top-2 duration-200">
                                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Replacement Staff</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Icons.UserPlus className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <select
                                            required
                                            value={editingRequest.replacementEmployeeId || ''}
                                            onChange={(e) => setEditingRequest({ ...editingRequest, replacementEmployeeId: e.target.value })}
                                            className="w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all py-2.5"
                                        >
                                            <option value="">Select a replacement</option>
                                            {staffList
                                                .filter(s => s.status !== 'deleted' && s.id !== editingRequest.employeeId)
                                                .map(staff => (
                                                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Reason (optional)</label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 text-gray-400">
                                        <Icons.FileText className="w-4 h-4" />
                                    </div>
                                    <textarea 
                                        value={editingRequest.reason || ''} 
                                        onChange={(e) => setEditingRequest({ ...editingRequest, reason: e.target.value })} 
                                        rows={2} 
                                        placeholder="Admin notes or reason..."
                                        className="block w-full pl-10 border-gray-200 dark:border-slate-700 dark:bg-slate-900/50 dark:text-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 sm:text-sm transition-all"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="pt-2 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setEditingRequest(null)}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2"
                                >
                                    <Icons.Save className="w-4 h-4" />
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

             {itemToDelete && (
                <ConfirmationModal
                    title="Delete Time Off Request"
                    message={`Are you sure you want to permanently delete the time off request for ${itemToDelete.staffName} from ${itemToDelete.startDate} to ${itemToDelete.endDate}?`}
                    confirmButtonText="Delete"
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                    isConfirming={isDeleting}
                />
            )}
        </div>
    );
};

export default TimeOffManagementView;
